import { useState } from 'react';
import { DataTable } from 'components/data-table';
import { DEFAULT_SCAN_DEPTH } from 'pages/admin/utils';
import type { ScanDepth } from 'pages/admin/utils';
import { jobClassColumns, sampledJobClassColumns } from './columns';
import { useQueueScan, useScanRequest } from './hooks';
import { ScanControls } from './scan-controls';
import { ErrorNote, ScanNote } from 'pages/admin/scan-note';
import styles from './page.module.scss';

interface QueueScanProps {
  /** The timed queues, from the summary (or the static fallback list). */
  queueNames: string[];
}

/**
 * On-demand job-class breakdown of one timed queue: count, oldest and newest
 * enqueued_at, and the most common argument values per class.
 *
 * @param {QueueScanProps} props - The queues that can be scanned.
 * @return The scan section.
 */
export function QueueScan({ queueNames }: QueueScanProps) {
  const [queueName, setQueueName] = useState<string | null>(null);
  const [scanDepth, setScanDepth] = useState<ScanDepth>(DEFAULT_SCAN_DEPTH);
  const { request, startScan } = useScanRequest<string>();
  const { data, isFetching, error } = useQueueScan(request);

  const selectedQueue = queueName ?? queueNames[0] ?? null;

  return (
    <section className={styles.section}>
      <h4 className={styles.sectionTitle}>Queue Contents by Job Class</h4>
      <p className={styles.sectionDescription}>
        Reads one queue on demand and groups its jobs by job class. A deep scan
        of a large queue can take several seconds.
      </p>
      <ScanControls
        idPrefix="queue-scan"
        targetLabel="Queue"
        targets={queueNames.map((name) => ({ id: name, label: name }))}
        target={selectedQueue}
        onTargetChange={setQueueName}
        scanDepth={scanDepth}
        onScanDepthChange={setScanDepth}
        onScan={() => {
          if (selectedQueue !== null) startScan(selectedQueue, scanDepth);
        }}
        loading={isFetching}
        buttonLabel="Scan Queue"
      />
      {error && <ErrorNote message={error.message} />}
      {/* Always rendered, so a screen reader hears each new result note. */}
      <div aria-live="polite" data-testid="queue-scan-note">
        {data && !isFetching && (
          <ScanNote
            totals={data}
            label={data.queue}
            options={{ newestFirst: true }}
          />
        )}
      </div>
      {data && !isFetching && (
        <DataTable
          data={data.byJobClass}
          columns={data.sampled ? sampledJobClassColumns : jobClassColumns}
          getRowId={(row) => row.jobClass}
          initialTableState={{ sortColumn: 'count', sortDirection: 'desc' }}
          config={{ sorting: true, globalSearch: true }}
        />
      )}
    </section>
  );
}
